import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify, getLatestSubmission, submissionStatusStyle, canClientSubmitTask, getStaffTaskNotes } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES, DEFAULT_DOC_TEMPLATES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { TasksTable } from '../tasks/TasksPage';
import { IconProperties } from '../../icons.properties';
import { Icon, InlineIcon } from '../../components/Icon';
import { listClientDocs, uploadClientDoc, publicDocUrl, resolveDocSrc } from '../../storage';
import { buildApiFilter, usePaginatedApiRows } from '../../core/searchFilters';
import { LoadMoreBar } from '../../components/LoadMoreBar';
import { useTableTools } from '../../core/tableView';
import { clientPortalUsesApiPaging } from '../../mobile/clientPortal';
import { SortableTh } from '../../components/SortableTh';

const CLIENT_TASK_ACCESSORS = {
  sequence: (t) => Number(t.sequence) || 0,
  title: (t) => (t.title || '').toLowerCase(),
  clientName: (t) => (t.clientName || '').toLowerCase(),
  status: (t) => t.status || '',
  dueDate: (t) => (t.dueDate ? new Date(t.dueDate).getTime() : 0),
};




function ClientTasks() {
  const { tasks, visibleCompanyIds } = useApp();
  const myTasks = tasks.filter(t => visibleCompanyIds.includes(t.clientId));
  return (
    <>
      <div style={{ marginBottom:14,fontSize:13,color:"var(--muted)" }}>Tasks across all your companies</div>
      <TasksTable tasks={myTasks} showClient={true} />
    </>
  );
}

function ClientTasksV2({ submissions, setSubs }) {
  const { tasks, clients, user, setTasks, openModal, showToast, dbSetTasks, visibleCompanyIds } = useApp();
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("dueDate");
  const [sortDir, setSortDir] = useState("asc");
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  // Tasks for the company(ies) currently in view (#12 switcher-aware)
  const myCompanyIds = visibleCompanyIds;
  const localAllClientTasks = tasks.filter(t => myCompanyIds.includes(t.clientId));
  const apiFilter = buildApiFilter({
    equals: myCompanyIds.length === 1 ? { company_id: myCompanyIds[0] } : {},
    order: "due_date.asc",
  });
  const apiPaging = clientPortalUsesApiPaging(myCompanyIds.length === 1);
  const { rows: allClientTasks, hasMore, loadMore, loadingMore } = usePaginatedApiRows("tasks", apiFilter, localAllClientTasks, { enabled: apiPaging });

  const clientTasks = (filter === "all" ? allClientTasks
    : filter === "pending" ? allClientTasks.filter(t => t.status === "open" || t.status === "client_action")
    : filter === "submitted" ? allClientTasks.filter(t => t.status === "team_approval")
    : filter === "completed" ? allClientTasks.filter(t => t.status === "completed")
    : allClientTasks)
    .filter(t => !search || (t.title || "").toLowerCase().includes(search.toLowerCase()) || (t.clientName || "").toLowerCase().includes(search.toLowerCase()));

  const table = useTableTools(clientTasks, {
    accessors: CLIENT_TASK_ACCESSORS,
    sortKey,
    sortDir,
    onSort: toggleSort,
    apiPaged: apiPaging,
  });
  const displayTasks = table.visible;

  return (
    <>
      <div style={{ display:"flex",gap:8,alignItems:"center",marginBottom:16 }}>
        <div className="chips" style={{margin:0}}>
          {[
            ["all", "All", allClientTasks.length],
            ["pending", "Pending", allClientTasks.filter(t=>t.status==="open"||t.status==="client_action").length],
            ["submitted", "Submitted", allClientTasks.filter(t=>t.status==="team_approval").length],
            ["completed", "Done", allClientTasks.filter(t=>t.status==="completed").length],
          ].map(([k,l,n]) => (
            <div key={k} className={`chip ${filter===k?"on":""}`} onClick={()=>setFilter(k)}>
              {l} ({n})
            </div>
          ))}
        </div>
      </div>

      <FilterBar search={search} setSearch={setSearch} searchPlaceholder="Search task / company…" onClear={() => setSearch("")} />

      <div className="card">
        <div className="card-head">
          <div className="card-title">My Tasks</div>
          <div style={{fontSize:12,color:"var(--muted)"}}>{clientTasks.length} task{clientTasks.length!==1?"s":""}</div>
        </div>
        {displayTasks.length === 0 ? (
          <div className="empty">
            <div className="empty-icon"><Icon name="check" size={28} /></div>
            <div style={{fontWeight:600,marginBottom:6}}>No tasks yet</div>
            <div style={{fontSize:13,color:"var(--muted)"}}>Tasks assigned to you will appear here</div>
          </div>
        ) : (
          <div className="table-scroll-shell">
          <div className="tbl-wrap tbl-m-billing">
            <table>
              <thead>
                <tr>
                  <SortableTh label="#" sortKey="sequence" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label="Task" sortKey="title" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label="Company" sortKey="clientName" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label="Status" sortKey="status" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label="Due Date" sortKey="dueDate" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <th>Action</th>
                  <th>Team Notes</th>
                </tr>
              </thead>
              <tbody>
                {displayTasks.map((t,idx) => {
                  const sub         = getLatestSubmission(submissions, t.id);
                  const docStatus   = submissionStatusStyle(sub?.status);
                  const staffNotes  = getStaffTaskNotes(t, sub);
                  const client      = clients.find(c => c.id === t.clientId);
                  const canSubmit   = canClientSubmitTask(t, sub, client);
                  const needsAction = canSubmit && t.status !== "team_approval";
                  const submitted   = t.status === "team_approval";
                  const completed   = t.status === "completed";
                  const isOverdue   = t.dueDate && new Date(t.dueDate) < new Date() && !completed;
                  return (
                    <tr key={t.id} style={{background:needsAction&&!submitted?"#FFFBEB":"inherit"}}>
                      <td style={{fontSize:11,color:"var(--muted)",fontWeight:700}}>
                        {t.sequence||idx+1}
                      </td>
                      <td>
                        <div style={{fontWeight:500,fontSize:13}}>{t.title}</div>
                        {needsAction && !submitted && (
                          <div style={{fontSize:10,color:"var(--orange)",marginTop:2,fontWeight:600}}>
                            Action required from you
                          </div>
                        )}
                      </td>
                      <td style={{fontSize:12,color:"var(--muted)"}}>{t.clientName||"—"}</td>
                      <td>
                        <Badge status={t.status}/>
                        {docStatus && (
                          <div style={{ fontSize: 10, fontWeight: 600, color: docStatus.color, background: docStatus.bg, padding: "2px 6px", borderRadius: 4, marginTop: 4, display: "inline-block" }}>
                            {docStatus.label}
                          </div>
                        )}
                      </td>
                      <td>
                        <span style={{fontSize:12,color:isOverdue?"var(--red)":"var(--muted)",fontWeight:isOverdue?700:400}}>
                          {t.dueDate ? new Date(t.dueDate).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}) : "—"}
                          {isOverdue && <> <Icon name="warning" size={12} color="var(--red)" /></>}
                        </span>
                      </td>
                      <td>
                        {canSubmit && !submitted && !completed && (
                          <button className="btn btn-primary btn-sm"
                            onClick={() => openModal("client-submit-task",{task:t})}>
                            {sub?.status === "changes_requested" ? "Resubmit →" : "Submit Docs →"}
                          </button>
                        )}
                        {!canSubmit && !completed && !submitted && (
                          <span style={{ fontSize: 12, color: "var(--muted)" }}>Awaiting team</span>
                        )}
                        {completed && (
                          <span style={{fontSize:12,color:"var(--green)",fontWeight:700}}><InlineIcon name="check" size={12} gap={4}>Complete</InlineIcon></span>
                        )}
                        {submitted && (
                          <span style={{fontSize:12,color:"#7C3AED",fontWeight:600}}><InlineIcon name="hourglass" size={12} gap={4}>Under Review</InlineIcon></span>
                        )}
                      </td>
                      <td className={staffNotes.length ? "client-task-staff-notes client-task-staff-notes--has" : "client-task-staff-notes"}>
                        {staffNotes.length === 0 ? (
                          <span style={{ fontSize: 12, color: "var(--faint)" }}>—</span>
                        ) : (
                          staffNotes.map(n => (
                            <div key={n.id} className={`client-task-staff-note client-task-staff-note--${n.kind}`}>
                              <div className="client-task-staff-note__meta">
                                {n.kind === "resubmit" ? (
                                  <InlineIcon name="warning" size={11} gap={4}>Resubmit required</InlineIcon>
                                ) : (
                                  <InlineIcon name="message" size={11} gap={4}>{n.by}</InlineIcon>
                                )}
                                {n.at && <span className="client-task-staff-note__at">{n.at}</span>}
                              </div>
                              <div className="client-task-staff-note__text">{n.text}</div>
                            </div>
                          ))
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </div>
        )}
        <LoadMoreBar
          hasMore={apiPaging ? hasMore : table.hasMore}
          loadingMore={loadingMore}
          onLoadMore={apiPaging ? loadMore : table.loadMore}
          count={table.shownCount}
        />
      </div>
    </>
  );
}

export { ClientTasks, ClientTasksV2 };
